import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { TherapyDto } from '../../../../../api/generated/model';
import {
  getGetTherapyByPatientQueryKey,
  useUpdateTherapyByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';

interface FormValues {
  conditionInterval: number | null;
}
export function SetConditionIntervalDialog({
  open,
  onClose,
  currentTherapy,
}: {
  open: boolean;
  onClose: () => void;
  currentTherapy: TherapyDto;
}) {
  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: { conditionInterval: currentTherapy.conditionIntervalDays ?? null },
  });

  const updateTherapyMutation = useUpdateTherapyByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetTherapyByPatientQueryKey(currentTherapy.patientId) });
        onClose();
      },
      onError: () => toast.error('Beim Anpassen des Rückfrage Intervals ist ein Fehler aufgetreten'),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        conditionInterval: currentTherapy.conditionIntervalDays ?? null,
      });
    }
  }, [open, currentTherapy]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 450 }}>
        <ModalClose />
        <Typography level={'h4'}>Rückfrage Intervall</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            updateTherapyMutation.mutate({
              id: currentTherapy.patientId,
              data: {
                id: currentTherapy.id,
                patientId: currentTherapy.patientId,
                therapyMedication: currentTherapy.therapyMedication,
                conditionIntervalDays: data.conditionInterval,
                therapyResult: currentTherapy.therapyResult,
                finished: currentTherapy.finished,
              },
            }),
          )}>
          <Stack gap={2}>
            <FormControl error={!!errors.conditionInterval}>
              <FormLabel>
                <Typography>Intervall</Typography>
              </FormLabel>
              <Controller
                name={'conditionInterval'}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Select value={value} onChange={(_, value) => onChange(value)}>
                    <Option value={7}>1 Woche</Option>
                    <Option value={14}>2 Wochen</Option>
                    <Option value={21}>3 Wochen</Option>
                    <Option value={30}>1 Monat</Option>
                  </Select>
                )}
              />
              {errors.conditionInterval && <FormHelperText>Interval muss angegeben werden</FormHelperText>}
            </FormControl>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={updateTherapyMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
